<template>
  <div>
    <b-modal
      id="AccessRoleModal"
      size="xl"
      content-class="menu-bg-blue-transparent-modal uar-modal"
      :title="`${isUpdate ? 'Update' : 'New'} Role Privileges`"
      hide-footer
     
      @show="initModal()"
    >
      <!-- Role name -->
      <b-row class="uar-role-row" no-gutters>
        <b-col cols="4">
          <div class="uar-field">
            <span class="uar-label">Role Name</span>
            <span v-if="isUpdate" class="uar-role-name">{{ updatedRoleName }}</span>
            <b-form-input
              v-else
              v-model="updatedRoleName"
              placeholder="Enter role name"
              class="uar-input"
            />
          </div>
        </b-col>
        <b-col cols="4">
          <div class="uar-field">
            <span class="uar-label">Organization</span>
            <b-form-select v-model="updatedOrg" class="uar-select">
              <template #first>
                <b-form-select-option :value="null" disabled>Select Organization</b-form-select-option>
              </template>
              <b-form-select-option
                v-for="org in organizations"
                :key="org.id"
                :value="org.id"
              >{{ org.company_name }}</b-form-select-option>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="4">
          <div class="uar-field">
            <span class="uar-label">Default Route</span>
            <b-form-select v-model="updatedRoute" class="uar-select">
              <template #first>
                <b-form-select-option :value="null" disabled>Select Default Route</b-form-select-option>
              </template>
              <b-form-select-option
                v-for="route in routes"
                :key="route.name"
                :value="route.value"
              >{{ route.name }} ({{ route.route }})</b-form-select-option>
            </b-form-select>
          </div>
        </b-col>
      </b-row>

      <!-- Tools -->
      <div class="uar-section">
        <div class="uar-section-header uar-section-header--tools" @click="toolsCollapsed = !toolsCollapsed">
          <div class="uar-section-title-row">
            <span class="uar-dot uar-dot--tools"></span>
            <span class="uar-section-title">MMP Tools</span>
          </div>
          <div class="uar-chevron" :class="{ open: toolsCollapsed }">
            <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
        <b-collapse v-model="toolsCollapsed">
          <div class="uar-module-grid" v-if="row">
            <label
              v-for="module in allModules.filter(m => m.module_type === 'tool')"
              :key="module.module_name"
              class="uar-checkbox-label"
              v-b-tooltip.hover.right="{ title: module.module_description, customClass: 'ac-tooltip' }"
            >
              <input type="checkbox" class="uar-checkbox" :value="{ id: module.id, module_name: module.module_name }" v-model="selectedModules" />
              <span class="uar-checkbox-custom"></span>
              <span class="uar-module-name">{{ module.module_name }}</span>
            </label>
          </div>
        </b-collapse>
      </div>

      <!-- Features -->
      <div class="uar-section">
        <div class="uar-section-header uar-section-header--features" @click="featuresCollapsed = !featuresCollapsed">
          <div class="uar-section-title-row">
            <span class="uar-dot uar-dot--features"></span>
            <span class="uar-section-title">MMP Features</span>
          </div>
          <div class="uar-chevron" :class="{ open: featuresCollapsed }">
            <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
        <b-collapse v-model="featuresCollapsed">
          <div class="uar-module-grid" v-if="row">
            <label
              v-for="module in allModules.filter(m => m.module_type === 'feature')"
              :key="module.module_name"
              class="uar-checkbox-label"
              v-b-tooltip.hover.right="{ title: module.module_description, customClass: 'ac-tooltip' }"
            >
              <input type="checkbox" class="uar-checkbox" :value="{ id: module.id, module_name: module.module_name }" v-model="selectedModules" />
              <span class="uar-checkbox-custom"></span>
              <span class="uar-module-name">{{ module.module_name }}</span>
            </label>
          </div>
        </b-collapse>
      </div>

      <!-- Admin -->
      <div class="uar-section">
        <div class="uar-section-header uar-section-header--admin" @click="adminCollapsed = !adminCollapsed">
          <div class="uar-section-title-row">
            <span class="uar-dot uar-dot--admin"></span>
            <span class="uar-section-title">MMP Administrative Capabilities</span>
          </div>
          <div class="uar-chevron" :class="{ open: adminCollapsed }">
            <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
        <b-collapse v-model="adminCollapsed">
          <div class="uar-module-grid" v-if="row">
            <label
              v-for="module in allModules.filter(m => m.module_type === 'admin')"
              :key="module.module_name"
              class="uar-checkbox-label"
              v-b-tooltip.hover.right="{ title: module.module_description, customClass: 'ac-tooltip' }"
            >
              <input type="checkbox" class="uar-checkbox" :value="{ id: module.id, module_name: module.module_name }" v-model="selectedModules" />
              <span class="uar-checkbox-custom"></span>
              <span class="uar-module-name">{{ module.module_name }}</span>
            </label>
          </div>
        </b-collapse>
      </div>

      <!-- Submit -->
      <div class="uar-submit-row">
        <b-button variant="primary" @click="handleSubmit()">Submit</b-button>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
/* ─── Role name row ──────────────────────────────────────────── */
.uar-role-row {
  padding: 4px 0 16px;
}
.uar-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 8px;
}
.uar-label {
  font-size: 13px;
  color: rgba(6, 60, 109, 0.7);
  white-space: nowrap;
}
.uar-role-name {
  font-size: 17px;
  font-weight: 600;
  color: #063c6d;
  padding-top: 3px;
}
.uar-input {
  background: #f3f5f8 !important;
  border: 1px solid rgba(6, 60, 109, 0.2) !important;
  color: #063c6d !important;
  border-radius: 4px;
}
.uar-input::-webkit-input-placeholder {
  color: rgba(6, 60, 109, 0.35) !important;
}
.uar-input:focus {
  background: #fff !important;
  border-color: #063c6d !important;
  box-shadow: 0 0 0 2px rgba(6, 60, 109, 0.12) !important;
}
.uar-select {
  background: #f3f5f8 !important;
  border: 1px solid rgba(6, 60, 109, 0.2) !important;
  color: #063c6d !important;
  border-radius: 4px;
}
.uar-select:focus {
  background: #fff !important;
  border-color: #063c6d !important;
  box-shadow: 0 0 0 2px rgba(6, 60, 109, 0.12) !important;
}
.uar-select option {
  background: #fff;
  color: #063c6d;
}

/* ─── Section wrapper ────────────────────────────────────────── */
.uar-section { margin-bottom: 6px; }

/* ─── Section headers ────────────────────────────────────────── */
.uar-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  margin-top: 6px;
  margin-bottom: 2px;
  transition: background 0.15s;
  border-left: 3px solid transparent;
  background: rgba(6, 60, 109, 0.04);
}
.uar-section-header:hover { background: rgba(6, 60, 109, 0.08); }
.uar-section-header--tools    { border-left-color: #0a5499; }
.uar-section-header--features { border-left-color: #1a7d59; }
.uar-section-header--admin    { border-left-color: #6c3cba; }

.uar-section-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.uar-section-title {
  font-size: 13.5px;
  font-weight: 500;
  color: #063c6d;
  letter-spacing: 0.01em;
}

/* ─── Dots ───────────────────────────────────────────────────── */
.uar-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.uar-dot--tools    { background: #0a5499; }
.uar-dot--features { background: #1a7d59; }
.uar-dot--admin    { background: #6c3cba; }

/* ─── Chevron ────────────────────────────────────────────────── */
.uar-chevron {
  width: 16px; height: 16px;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.2s ease;
  color: rgba(6, 60, 109, 0.55);
  opacity: 0.7;
}
.uar-chevron.open { transform: rotate(180deg); }
.uar-chevron svg {
  width: 13px; height: 13px;
  stroke: currentColor; fill: none;
  stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
}

/* ─── Module grid & checkboxes ───────────────────────────────── */
.uar-module-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 12px 4px 16px 18px;
}
.uar-checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 12.5px;
  color: #063c6d;
  margin: 0;
}
.uar-checkbox { display: none; }
.uar-checkbox-custom {
  width: 14px; height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(6, 60, 109, 0.3);
  background: rgba(6, 60, 109, 0.04);
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
  position: relative;
}
.uar-checkbox:checked + .uar-checkbox-custom {
  background: #063c6d;
  border-color: #063c6d;
}
.uar-checkbox:checked + .uar-checkbox-custom::after {
  content: '';
  position: absolute;
  left: 3px; top: 1px;
  width: 5px; height: 8px;
  border: 1.5px solid #fff;
  border-top: none; border-left: none;
  transform: rotate(45deg);
}
.uar-module-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ─── Submit row ─────────────────────────────────────────────── */
.uar-submit-row {
  display: flex;
  justify-content: center;
  padding: 16px 0 4px;
}
</style>

<style>
.uar-modal .menu-bg-blue-transparent-modal {
  background: transparent !important;
  color: #063c6d !important;
  text-shadow: none !important;
}
</style>

<script>
import axios from 'axios'
export default {
    name: 'MMP_UpdateAccessRole',
    props: [
        'row',
        'isUpdate',
        'user',
        'allModules',
        'organizations'
    ],
    data() {
      return {
        selectedModules: [],
        updatedRoleName: '',
        updatedOrg: null,
        updatedRoute: null,
        submitErrors: [],
        status: true,
        toolsCollapsed: true,
        featuresCollapsed: true,
        adminCollapsed: true,
        routes: [
          {
            name: 'MMP',
            route: '/mmp',
            value: 'MMP'
          },
          {
            name: 'Spatial Intelligence (AI)',
            route: '/Spatial_Intelligence',
            value: 'Spatial_Intelligence'
          },
          {
            name: 'Data Tools',
            route: '/data_tools',
            value: 'DataTools'
          },
          {
            name: 'Data Delivery Tool',
            route: '/data_delivery_tool',
            value: 'DDT'
          },
          {
            name: 'Sales Tool',
            route: '/sales',
            value: 'Sales'
          },
          {
            name: 'Trail Review Tool',
            route: '/trail_review_tool',
            value: 'TRT'
          } 
        ]
      }
    },
    methods: {
      initModal() {
        // this.getCompanies();
      },
      handleSubmit() {
        this.isUpdate ? this.submitUpdate() : this.submitNew();
      },
      formValidation() {
        this.submitErrors = []
        if (this.updatedRoleName == '' || this.updatedRoleName == null) {
            this.submitErrors.push('Role Name')
            this.$bvToast.toast('Role Name cannot be null.', {
                autoHideDelay: 7000,
                variant: 'danger',
                title: 'New Role Submit Error',
                toaster: 'b-toaster-top-right'
            })
        }
        if (this.selectedModules.length == 0) {
            this.submitErrors.push('MMP Modules')
            this.$bvToast.toast('Role must have at least 1 module.', {
                autoHideDelay: 7000,
                variant: 'danger',
                title: `${this.isUpdate ? 'Update' : 'New'} Role Submit Error`,
                toaster: 'b-toaster-top-right'
            })
        }

        return this.submitErrors.length
      },
      submitNew() {
        let mods = this.selectedModules.map((obj) => obj.id)
        let numErr = this.formValidation()

        if (numErr != 0) {
            return;
        }

        if (sessionStorage.token) {
            axios.get(process.env.API_LOCATION + '/api/users/newRole', {
                headers: {
                    'x-auth-token': sessionStorage.token
                },
                params: {
                    modules: mods,
                    role_name: this.updatedRoleName,
                    org: this.updatedOrg,
                    route: this.updatedRoute,
                    user_id: this.user.id
                }
            }).then(response => {
                const content = response;

                this.$bvToast.toast(content.data.msg, {
                    autoHideDelay: 7000,
                    variant: 'success',
                    title: 'New Role Status',
                    toaster: 'b-toaster-top-right'
                })

                this.$emit('refresh')
                this.$bvModal.hide('AccessRoleModal')
            })
            .catch(function (error) {
                console.log(error)
            })
        } else {
            console.log('No User Token')
        }
      },
      submitUpdate() {
        let mods = this.selectedModules.map((obj) => obj.id)
        let numErr = this.formValidation();

        if (numErr != 0) {
            return;
        }

        if (sessionStorage.token) {
            axios.get(process.env.API_LOCATION + '/api/users/updateRole', {
                headers: {
                    'x-auth-token': sessionStorage.token
                },
                params: {
                    id: this.row.user_role_id,
                    modules: mods,
                    org: this.updatedOrg,
                    route: this.updatedRoute,
                    user_id: this.user.id
                }
            }).then(response => {
                const content = response;

                this.$bvToast.toast(content.data.msg, {
                    autoHideDelay: 7000,
                    variant: 'success',
                    title: 'Update Role Status',
                    toaster: 'b-toaster-top-right'
                })

                this.$emit('refresh')
                this.$emit('refreshUserModules')
                this.$bvModal.hide('AccessRoleModal')
            })
            .catch(function (error) {
                console.log(error)
            })
        } else {
            console.log('No User Token')
        }
      }
    },
    watch: {
        row: function (newValue) {
            if (newValue && newValue.role_name) {
                this.selectedModules = newValue.mmp_user_modules.map((id, index) => ({
                    id,
                    module_name: newValue.module_names[index]
                }));
                this.updatedRoleName = newValue.role_name;
                this.updatedOrg = newValue.org;
                this.updatedRoute = newValue.route;
                this.status = newValue.isactive;
            } else {
                this.selectedModules = []
                this.updatedRoleName = ''
                this.updatedOrg = null;
                this.updatedRoute = null;
                this.status = false
            }
        }
    }
}
</script>
